import { BannerNotification } from '@hologram-dimension/banner-notification';
import { Button } from '@hologram-dimension/button';
import { Link } from '@hologram-dimension/link';
import { ModalHeader } from 'common-js/components';
import { SETTING_DATA_LIMITS_HELP } from 'common-js/constants/links';
import BulkUpdateOverageForm from 'common-js/forms/BulkUpdateOverageForm';
import useAppDispatch from 'common-js/hooks/useAppDispatch';
import useAppSelector from 'common-js/hooks/useAppSelector';
import { getCachedDevices, getSelectedLoadedDevices } from 'common-js/reducers/devices/selectors';
import { close as closeModal } from 'common-js/reducers/modal/actions';
import { getPostPay } from 'common-js/reducers/organization/selectors';
import { Device } from 'common-js/types/Device';
import { type FC, useCallback, useMemo, useState } from 'react';

interface DisclaimerProps {
  hasPostpay?: boolean;
}

const Disclaimer: FC<DisclaimerProps> = ({ hasPostpay }) => {
  if (hasPostpay) {
    return (
      <ul className="BulkDataActionModal__disclaimer">
        <li>
          A SIM <strong>could be charged overage data</strong> if it hits a data usage limit while
          an active data session is open, as the session cannot be closed automatically.
        </li>
        <li>
          SIMs on Pay Monthly <strong>data plans with included data</strong> can only have data
          limits above the included data amount. Data usage above the included data amount is
          charged at the overage data rate. Data is billed per KB, until your SIMs are paused.
        </li>
      </ul>
    );
  }

  return (
    <ul className="BulkDataActionModal__disclaimer">
      <li>
        Data usage limits are tracked <strong>per SIM’s 30 day renewal period</strong>, but your
        data usage totals reflect monthly billing.
      </li>
      <li>
        A SIM <strong>could be charged overage data</strong> if it hits a data usage limit while an
        active data session is open, as the session cannot be closed automatically.
      </li>
      <li>
        SIMs on Pay Monthly <strong>data plans with included data</strong> can only have data limits
        above the included data amount. Data usage above the included data amount is charged at the
        overage data rate. Data is billed per KB, until your SIMs are paused.
      </li>
    </ul>
  );
};

interface BulkDataUsageLimitsModalProps {
  deviceOverrides?: Array<DeviceId>;
}

const BulkDataUsageLimitsModal: FC<BulkDataUsageLimitsModalProps> = ({ deviceOverrides }) => {
  const [confirmedDisclaimer, setConfirmedDisclaimer] = useState(false);
  const hasPostpay = useAppSelector(getPostPay);

  const dispatch = useAppDispatch();
  const selectedDevices = useAppSelector(getSelectedLoadedDevices);
  const loadedDevices: Record<DeviceId, Device> | undefined = useAppSelector(getCachedDevices);

  const devicesFromOverrides: Array<Device> | undefined = useMemo(() => {
    if (deviceOverrides && loadedDevices) {
      return Object.entries(loadedDevices)
        .filter(([id]) => deviceOverrides.includes(parseInt(id, 10)))
        .map(([, device]) => device);
    }

    return undefined;
  }, [deviceOverrides, loadedDevices]);

  const devices = useMemo(
    () => devicesFromOverrides ?? selectedDevices,
    [devicesFromOverrides, selectedDevices]
  );

  const onCancel = useCallback(() => {
    dispatch(closeModal());
  }, [dispatch]);

  const confirmDisclaimer = useCallback(() => {
    setConfirmedDisclaimer(true);
  }, []);

  return (
    <>
      <ModalHeader title="Set data usage limit" />
      <div className="BulkDataActionModal__info-panel">
        A data usage limit is a cap, or amount of data, that when reached, pauses data usage for a
        SIM.{' '}
        <Link href={SETTING_DATA_LIMITS_HELP} type="inline" target="_blank" rel="noreferrer">
          Learn more
        </Link>
      </div>
      <div className="BulkDataActionModal__contents">
        {!confirmedDisclaimer && <Disclaimer hasPostpay={hasPostpay} />}
        {confirmedDisclaimer &&
          (devices && devices.length > 0 ? (
            <BulkUpdateOverageForm
              devices={devices}
              onCancel={() => {
                dispatch(closeModal());
              }}
            />
          ) : (
            <BannerNotification variant="system-error">
              No devices were found to update. Please try again
            </BannerNotification>
          ))}
      </div>
      {!confirmedDisclaimer && (
        <div className="BulkDataActionModal__footer">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={confirmDisclaimer}>Acknowledge</Button>
        </div>
      )}
    </>
  );
};

export default BulkDataUsageLimitsModal;
